import { Tooltip } from '@/components/atoms/Tooltip';
import type { FilterProps } from '@/components/molecules/Filters';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import {
	FlatArea,
	FlatCalendar,
	FlatNumberBook,
	InfoWhite,
	MoneyPile
} from '@/resources/assets/images';
import { useMemo } from 'react';

export type SummarySessionProps = Pick<FilterProps, 'appliedFilters'> &
	Pick<Business, 'yearIncorporated' | 'yearInBusiness' | 'customData'>;

export const SummarySession = ({
	appliedFilters,
	yearIncorporated,
	yearInBusiness,
	customData
}: SummarySessionProps) => {
	const parsedYearIncorporated = useMemo(() => {
		if (yearIncorporated) return yearIncorporated;

		if (!yearInBusiness) return null;

		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();

		return currentYear - yearInBusiness;
	}, [yearInBusiness, yearIncorporated]);

	const isEmployeesMatch = useMemo(() => {
		if (!customData?.numEmployees || !appliedFilters) return false;

		const employeesMatchMap = {
			employees1To49: '1 to 49',
			employees50To99: '50 to 99',
			employees100To249: '100 to 249',
			employees250To499: '250 to 499',
			employeesOver500: '500+'
		};

		const isEmployeesInFilters = Boolean(
			Object.entries(employeesMatchMap).find(
				([key, value]) => key in appliedFilters && value
			)
		);

		return isEmployeesInFilters;
	}, [appliedFilters, customData?.numEmployees]);

	const isSalesVolumeMatch = useMemo(() => {
		if (!customData?.salesVolume || !appliedFilters) return false;

		const salesMatchMap = {
			salesVolumeLessThan5M: 'Less than $5 million',
			salesVolume5To10M: '$5 to 10 million',
			salesVolume10To20M: '$10 to 20 million',
			salesVolume20To50M: '$20 to 50 million',
			salesVolume50To100M: '$50 to 100 million',
			salesVolumeOver100M: 'Over $100 million'
		};

		const isSalesInFilters = Boolean(
			Object.entries(salesMatchMap).find(
				([key, value]) => key in appliedFilters && value
			)
		);

		return isSalesInFilters;
	}, [appliedFilters, customData?.salesVolume]);

	const isSqftRangeMatch = useMemo(() => {
		if (
			!customData?.sqftRangeEstimateMax ||
			!customData?.sqftRangeEstimateMin ||
			Number.isNaN(customData.sqftRangeEstimateMax) ||
			Number.isNaN(customData.sqftRangeEstimateMin)
		)
			return false;

		const { sqftSmall, sqftMedium, sqftLarge } = appliedFilters ?? {};

		const sqftMin = Number(customData.sqftRangeEstimateMin);
		const sqftMax = Number(customData.sqftRangeEstimateMax);

		return (
			(sqftSmall && sqftMin >= 1 && sqftMax <= 2.5 * 1_000) ||
			(sqftMedium && sqftMin > 2.5 * 1_000 && sqftMax <= 10 * 1_000) ||
			(sqftLarge && sqftMin > 10 * 1_000)
		);
	}, [
		appliedFilters,
		customData?.sqftRangeEstimateMax,
		customData?.sqftRangeEstimateMin
	]);

	return (
		<div className="grid grid-cols-2 gap-x-1.5 gap-y-6 p-1">
			{parsedYearIncorporated && (
				<section className="flex flex-col gap-1">
					<header className="flex items-center gap-1 text-dm-mid-grey">
						<FlatCalendar />

						<span className="inline-flex items-center gap-2">
							<p className="text-xs font-semibold md:text-base">
								Year Incorporated
							</p>

							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>
										<InfoWhite />
									</Tooltip.Trigger>
									<Tooltip.Content className="text-sm text-dm-mid-grey">
										Information reported on most recent
										public corporate filings or estimated
										based on Resquared models otherwise.
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						</span>
					</header>

					<p className="text-sm font-semibold text-dm-white md:text-base">
						{parsedYearIncorporated}
					</p>
				</section>
			)}

			{customData?.numEmployees && (
				<section className="flex flex-col gap-1">
					<header className="flex items-center gap-1 text-dm-mid-grey">
						<FlatNumberBook />

						<span className="inline-flex items-center gap-2">
							<p className="text-xs font-semibold md:text-base">
								# of Employees
							</p>

							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>
										<InfoWhite />
									</Tooltip.Trigger>
									<Tooltip.Content className="text-sm text-dm-mid-grey">
										Information reported on most recent
										public corporate filings or estimated
										based on Resquared models otherwise.
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						</span>
					</header>

					<p
						data-is-match={isEmployeesMatch}
						className="text-sm font-semibold text-dm-white data-[is-match=true]:text-dm-gold md:text-base"
					>
						{customData.numEmployees}
					</p>
				</section>
			)}

			{customData?.salesVolume && (
				<section className="flex flex-col gap-1">
					<header className="flex items-center gap-1 text-dm-mid-grey">
						<MoneyPile />

						<span className="inline-flex items-center gap-2">
							<p className="text-xs font-semibold md:text-base">
								Sales Volume
							</p>

							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>
										<InfoWhite />
									</Tooltip.Trigger>
									<Tooltip.Content className="text-sm text-dm-mid-grey">
										Information reported on most recent
										public corporate filings or estimated
										based on Resquared models otherwise.
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						</span>
					</header>

					<p
						data-is-match={isSalesVolumeMatch}
						className="text-sm font-semibold text-dm-white data-[is-match=true]:text-dm-gold md:text-base"
					>
						{customData.salesVolume}
					</p>
				</section>
			)}

			{customData?.sqftRangeEstimateMax &&
				customData?.sqftRangeEstimateMin && (
					<section className="flex flex-col gap-1">
						<header className="flex items-center gap-1 text-dm-mid-grey">
							<FlatArea />

							<span className="inline-flex items-center gap-2">
								<p className="text-xs font-semibold md:text-base">
									Square Feet
								</p>

								<Tooltip.Provider>
									<Tooltip.Root>
										<Tooltip.Trigger>
											<InfoWhite />
										</Tooltip.Trigger>
										<Tooltip.Content className="text-sm text-dm-mid-grey">
											{
												customData?.sqftRangeEstimateReasoning
											}
										</Tooltip.Content>
									</Tooltip.Root>
								</Tooltip.Provider>
							</span>
						</header>

						<p
							data-is-match={isSqftRangeMatch}
							className="text-sm font-semibold text-dm-white data-[is-match=true]:text-dm-gold md:text-base"
						>
							{customData?.sqftRangeEstimateMin} sq. ft -{' '}
							{customData?.sqftRangeEstimateMax} sq. ft
						</p>
					</section>
				)}
		</div>
	);
};
